import * as React from "react";

import { Button, Grid, Table, Form, Loader } from "semantic-ui-react";
import { Input } from "@neworbit/simpleui-input";
import { flatten, isEqual } from "lodash";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { isRequestActive } from "redux-request-loading";

import { customersSelector } from "../selectors";
import { ADMIN_ROLES, Authorize } from "../../../auth";
import { ApplicationState } from "../../../applicationState";
import { Team } from "../../../teams/model";
import { teamsSelector } from "../../../teams/selectors";
import { loadTeams } from "../../../teams/actions";
import { loadCustomers } from "../actions";
import { LOAD_CUSTOMERS } from "../actiontypes";
import { CustomerInRedux } from "..";

import { Link, replace } from "redux-little-router";

export const All = () => {
    const [name, setName] = React.useState("");
    const [selectedTeams, setSelectedTeams] = React.useState([] as Team[]);
    const [matchingCustomers, setMatchingCustomers] = React.useState([] as CustomerInRedux[]);

    const path = useSelector((state: ApplicationState) => state.router.pathname);
    const customers = useSelector(customersSelector);
    const teams = useSelector(teamsSelector);
    const loadingCustomers = useSelector((state: ApplicationState) => isRequestActive(state, LOAD_CUSTOMERS));

    const dispatch = useDispatch();

    const onFilterValueChange = React.useCallback((value: string) => {
        setName(value);
        dispatch(replace(`customers?name=${value}`));
        dispatch(loadCustomers());
    }, [dispatch]);

    const onTeamsChanged = React.useCallback((teamIds: string[]) => {
        const newlySelectedTeams = teamIds.map(teamId => teams.find(team => team.id === teamId));

        if (!isEqual(newlySelectedTeams, selectedTeams)) {
            setSelectedTeams(newlySelectedTeams.filter(x => !!x));
        }
    }, [selectedTeams, teams]);

    React.useEffect(() => {
        dispatch(loadCustomers());
        dispatch(loadTeams());
    }, [dispatch]);

    React.useEffect(() => {
        if (selectedTeams.length === 0) {
            setMatchingCustomers(customers);
        } else {
            const flattenedCustomerIds = flatten(selectedTeams.map(t => t.customers.map(c => c.id)));
            setMatchingCustomers(customers.filter(customer => flattenedCustomerIds.includes(customer.id)));
        }
    }, [customers, selectedTeams]);

    return (
        <Grid container className="nomargintop">
            <Grid.Row>
                <Grid.Column width={14}>
                    <h1>Customers</h1>
                </Grid.Column>
                <Grid.Column width={2} textAlign="right">
                    <Authorize roles={[ADMIN_ROLES.SYSTEM_ADMIN]}>
                        <Button icon="add" content="Create" as={Link} href={`${path}/create`} />
                    </Authorize>
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column width={16}>
                    <Form>
                        <Grid columns="2">
                            <Grid.Column>
                                <Input.Text
                                    value={name}
                                    onChange={onFilterValueChange}
                                    label="Name"
                                />
                            </Grid.Column>
                            <Grid.Column>
                                <Input.DropdownMulti
                                    dynamicOptions
                                    label="Teams"
                                    options={teams.map(team => ({ key: team.id, value: team.id, text: team.name }))}
                                    onChange={onTeamsChanged}
                                    value={selectedTeams.map(team => team.id)}
                                    search
                                />
                            </Grid.Column>
                        </Grid>
                    </Form>
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column width={16}>
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Name</Table.HeaderCell>
                                <Table.HeaderCell>Prefix</Table.HeaderCell>
                                <Table.HeaderCell>Email Address</Table.HeaderCell>
                                <Table.HeaderCell>Active</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {loadingCustomers && <Loader active />}
                            {!loadingCustomers && matchingCustomers && matchingCustomers.map(customer => (
                                <Table.Row key={customer.id}>
                                    <Table.Cell>
                                        <Link href={`${path}/${customer.id}`}>
                                            {customer.name}
                                        </Link>
                                    </Table.Cell>

                                    <Table.Cell>
                                        {customer.prefix}
                                    </Table.Cell>

                                    <Table.Cell>
                                        {customer.emailAddress}
                                    </Table.Cell>

                                    <Table.Cell>
                                        {JSON.stringify(customer.active)}
                                    </Table.Cell>

                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};
